import { supabase } from './supabase';
import { auditTrailService } from './auditTrailService';

export const sessionManagementService = {
  // Create new session record
  async createSession({
    userId,
    gymId,
    sessionToken,
    deviceType,
    deviceName,
    browserName,
    osName,
    ipAddress,
    location,
    userAgent
  }) {
    try {
      const { data, error } = await supabase?.from('user_sessions')?.insert({
          user_id: userId,
          gym_id: gymId,
          session_token: sessionToken,
          device_type: deviceType,
          device_name: deviceName,
          browser_name: browserName,
          os_name: osName,
          ip_address: ipAddress,
          location: location,
          user_agent: userAgent,
          is_active: true
        })?.select()?.single();

      if (error) throw error;

      // Log session creation
      await auditTrailService?.logAuditEvent({
        gymId,
        userId,
        eventType: 'user_authentication',
        eventCategory: 'security',
        eventAction: 'session_created',
        eventDescription: `New session created from ${deviceType} - ${location || 'Unknown location'}`,
        severity: 'info',
        ipAddress,
        metadata: { sessionId: data?.id, deviceType, browserName }
      });

      return { data, error: null };
    } catch (error) {
      console.error('Error creating session:', error);
      return { data: null, error };
    }
  },

  // Get all active sessions for a user
  async getUserSessions(userId) {
    try {
      const { data, error } = await supabase?.from('user_sessions')?.select('*')?.eq('user_id', userId)?.eq('is_active', true)?.order('last_activity', { ascending: false });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching user sessions:', error);
      return { data: null, error };
    }
  },

  // Get all sessions (active and inactive) for a user
  async getAllUserSessions(userId, limit = 50) {
    try {
      const { data, error } = await supabase?.from('user_sessions')?.select('*')?.eq('user_id', userId)?.order('login_time', { ascending: false })?.limit(limit);

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching all user sessions:', error);
      return { data: null, error };
    }
  },

  // Update session activity timestamp
  async updateSessionActivity(sessionId) {
    try {
      const { data, error } = await supabase?.from('user_sessions')?.update({ last_activity: new Date()?.toISOString() })?.eq('id', sessionId)?.select()?.single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating session activity:', error);
      return { data: null, error };
    }
  },

  // Logout specific session (remote logout)
  async logoutSession(sessionId, userId, reason = 'User initiated logout') {
    try {
      const { data, error } = await supabase?.from('user_sessions')?.update({
          is_active: false,
          logout_time: new Date()?.toISOString(),
          logout_reason: reason
        })?.eq('id', sessionId)?.eq('user_id', userId)?.select()?.single();

      if (error) throw error;

      // Log session termination
      await auditTrailService?.logAuditEvent({
        gymId: data?.gym_id,
        userId,
        eventType: 'user_authentication',
        eventCategory: 'security',
        eventAction: 'session_terminated',
        eventDescription: `Session terminated: ${reason}`,
        severity: 'warning',
        ipAddress: data?.ip_address,
        metadata: { sessionId, reason, deviceType: data?.device_type }
      });

      return { data, error: null };
    } catch (error) {
      console.error('Error logging out session:', error);
      return { data: null, error };
    }
  },

  // Logout all sessions except current
  async logoutAllOtherSessions(userId, currentSessionId) {
    try {
      const { data, error } = await supabase?.from('user_sessions')?.update({
          is_active: false,
          logout_time: new Date()?.toISOString(),
          logout_reason: 'Logged out from all other devices'
        })?.eq('user_id', userId)?.eq('is_active', true)?.neq('id', currentSessionId)?.select();

      if (error) throw error;

      // Log bulk session termination
      await auditTrailService?.logAuditEvent({
        userId,
        eventType: 'user_authentication',
        eventCategory: 'security',
        eventAction: 'bulk_session_termination',
        eventDescription: `User logged out from ${data?.length || 0} other devices`,
        severity: 'warning',
        metadata: { terminatedCount: data?.length }
      });

      return { data, error: null };
    } catch (error) {
      console.error('Error logging out all other sessions:', error);
      return { data: null, error };
    }
  },

  // Logout all sessions for a user
  async logoutAllSessions(userId, reason = 'Security action - all sessions terminated') {
    try {
      const { data, error } = await supabase?.from('user_sessions')?.update({
          is_active: false,
          logout_time: new Date()?.toISOString(),
          logout_reason: reason
        })?.eq('user_id', userId)?.eq('is_active', true)?.select();

      if (error) throw error;

      // Log security action
      await auditTrailService?.logAuditEvent({
        userId,
        eventType: 'security_event',
        eventCategory: 'security',
        eventAction: 'all_sessions_terminated',
        eventDescription: reason,
        severity: 'critical',
        metadata: { terminatedCount: data?.length }
      });

      return { data, error: null };
    } catch (error) {
      console.error('Error logging out all sessions:', error);
      return { data: null, error };
    }
  },

  // Get session statistics
  async getSessionStatistics(userId) {
    try {
      const { data: activeSessions, error: activeError } = await supabase?.from('user_sessions')?.select('*', { count: 'exact', head: false })?.eq('user_id', userId)?.eq('is_active', true);

      if (activeError) throw activeError;

      // Get unique devices
      const uniqueDevices = new Set(activeSessions?.map(s => s?.device_type))?.size || 0;

      // Get unique locations
      const uniqueLocations = new Set(
        activeSessions?.filter(s => s?.location)?.map(s => s?.location)
      )?.size || 0;

      // Get most recent login
      const mostRecentLogin = activeSessions?.length > 0
        ? activeSessions?.reduce((latest, session) => {
            return new Date(session?.login_time) > new Date(latest?.login_time) ? session : latest;
          })
        : null;

      return {
        data: {
          totalActiveSessions: activeSessions?.length || 0,
          uniqueDevices,
          uniqueLocations,
          mostRecentLogin,
          sessions: activeSessions
        },
        error: null
      };
    } catch (error) {
      console.error('Error fetching session statistics:', error);
      return { data: null, error };
    }
  },

  // Detect device type from user agent
  detectDeviceType(userAgent) {
    if (!userAgent) return 'unknown';
    
    const ua = userAgent?.toLowerCase();
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i?.test(ua)) {
      return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/?.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  },

  // Parse browser name from user agent
  parseBrowserName(userAgent) {
    if (!userAgent) return 'Unknown';
    
    if (userAgent?.includes('Firefox')) return 'Firefox';
    if (userAgent?.includes('Chrome')) return 'Chrome';
    if (userAgent?.includes('Safari') && !userAgent?.includes('Chrome')) return 'Safari';
    if (userAgent?.includes('Edge')) return 'Edge';
    if (userAgent?.includes('Opera') || userAgent?.includes('OPR')) return 'Opera';
    return 'Unknown';
  },

  // Parse OS name from user agent
  parseOSName(userAgent) {
    if (!userAgent) return 'Unknown';
    
    if (userAgent?.includes('Windows')) return 'Windows';
    if (userAgent?.includes('Mac OS')) return 'macOS';
    if (userAgent?.includes('Linux')) return 'Linux';
    if (userAgent?.includes('Android')) return 'Android';
    if (userAgent?.includes('iOS') || userAgent?.includes('iPhone') || userAgent?.includes('iPad')) return 'iOS';
    return 'Unknown';
  }
};